import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrip, getTripMeta, addRound, deleteRound, deleteTrip, patchPlayerActive, rotateUserPin, patchTrip } from '../lib/api';
import api from '../lib/api';
import { removeAccessedCode } from '../lib/tripHistory';
import { getTripAccess, setTripAccess } from '../lib/tripAccess';
import TripAccessContext from '../lib/TripAccessContext';
import TripPasswordGate from './TripPasswordGate';
import ScoringHomeScreenTip, { shouldShowHomeScreenTip } from '../components/ScoringHomeScreenTip';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, UsersThree, ListNumbers, Trophy, Plus, Copy, ShareNetwork, Trash, CaretRight, Golf, QrCode, Lock, LockOpen, Eye, ShieldCheck, Pencil } from '@phosphor-icons/react';
import { QRCodeSVG } from 'qrcode.react';
import TripSetupWizard, { WIZARD_RETURN_KEY } from './TripSetupWizard';
import { toast } from 'sonner';
import { roundHasScores } from '../lib/formatHelpers';
import { useAuth } from '../lib/authContext';
import TripTeamsSection from './TripTeamsSection';
import DivisionsEditor from '../components/DivisionsEditor';
import PrizePot from './PrizePot';
import EventBrandingCard from '../components/EventBrandingCard';
import { formatHandicapIndex, displayWHS, isRoundFrozen } from '../golf';

import EventBranding from '../components/EventBranding';

const HERO_IMG = 'https://images.unsplash.com/photo-1543105177-748ceda71741?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200';
const EVENT_LABELS = { trip: 'Golf Trip', society: 'Society Day', casual: 'Casual Round' };
const JOIN_BASE = process.env.REACT_APP_PUBLIC_URL || '';

export default function TripDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const cacheKey = `trip_${code}`;
  const [trip, setTrip] = useState(() => {
    try { const c = sessionStorage.getItem(cacheKey); return c ? JSON.parse(c) : null; } catch { return null; }
  });
  const [loading, setLoading] = useState(!trip);
  const updateTrip = useCallback((data) => {
    setTrip(data);
    try { sessionStorage.setItem(cacheKey, JSON.stringify(data)); } catch {}
    // Sync branding state
    if (data?.branding?.logo_url) setBrandingImageUrl(data.branding.logo_url);
    else setBrandingImageUrl(null);
    if (data?.branding?.cover_url) setCoverImageUrl(data.branding.cover_url);
    else setCoverImageUrl(null);
  }, [cacheKey]);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [togglingPlayer, setTogglingPlayer] = useState(null);
  const [showWizard, setShowWizard] = useState(null);
  const [rotatePinOpen, setRotatePinOpen] = useState(false);
  const [newPin, setNewPin] = useState('');
  const [rotateLoading, setRotateLoading] = useState(false);
  const [rotateSuccess, setRotateSuccess] = useState(null); // stores rotated PIN on success
  const [rotateError, setRotateError] = useState('');
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  // Access control state
  const [accessChecked, setAccessChecked] = useState(false);
  const [needsGate, setNeedsGate] = useState(false);
  const [showHomeScreenTip, setShowHomeScreenTip] = useState(false);
  const [role, setRole] = useState(null); // 'organiser' | 'user'
  const [legacy, setLegacy] = useState(false);
  const [rotatePasswordOpen, setRotatePasswordOpen] = useState(false);
  const [rotatePasswordLoading, setRotatePasswordLoading] = useState(false);
  const [rotatePasswordResult, setRotatePasswordResult] = useState(null);
  const [brandingImageUrl, setBrandingImageUrl] = useState(null);
  const [coverImageUrl, setCoverImageUrl] = useState(null);

  const isOrganiser = role === 'organiser';
  const { organiser } = useAuth();
  const isSignedInOrganiser = !!(organiser?.id && trip?.organiser_id && organiser.id === trip.organiser_id);
  const isOwner = !!(getTripAccess(code)?.legacy); // legacy unlimited = owner

  // Step 1: Check if trip needs password gate
  useEffect(() => {
    (async () => {
      try {
        const { data: meta } = await getTripMeta(code);
        if (!meta.exists) { setError('Event not found'); setLoading(false); return; }
        if (meta.branding?.logo_url) setBrandingImageUrl(meta.branding.logo_url);
        if (meta.branding?.cover_url) setCoverImageUrl(meta.branding.cover_url);
        if (meta.legacy_unlimited) {
          // Legacy trip: auto-grant organiser, store for future requests
          setRole('organiser');
          setLegacy(true);
          setTripAccess(code, 'organiser', null, true);
          setAccessChecked(true);
          return;
        }
        // Non-legacy: check localStorage
        const stored = getTripAccess(code);
        if (stored?.role) {
          setRole(stored.role);
          setLegacy(stored.legacy || false);
          setAccessChecked(true);
        } else {
          setNeedsGate(true);
          setLoading(false);
        }
      } catch {
        setError('Failed to check trip');
        setLoading(false);
      }
    })();
  }, [code]);

  // Step 2: Load trip data once access is granted
  const load = useCallback(async () => {
    try {
      const res = await getTrip(code);
      updateTrip(res.data);
      if (res.data.branding?.logo_url) setBrandingImageUrl(res.data.branding.logo_url);
      if (res.data.branding?.cover_url) setCoverImageUrl(res.data.branding.cover_url);
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.code === 'invalid_trip_access') {
        setNeedsGate(true);
        setRole(null);
      } else {
        setError('Event not found');
      }
    } finally { setLoading(false); }
  }, [code, updateTrip]);

  useEffect(() => {
    if (accessChecked) load();
  }, [accessChecked, load]);

  // Listen for trip-access-invalid events (from API interceptor)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.code === code.toUpperCase()) {
        setNeedsGate(true);
        setRole(null);
        setTrip(null);
      }
    };
    window.addEventListener('trip-access-invalid', handler);
    return () => window.removeEventListener('trip-access-invalid', handler);
  }, [code]);

  // Wizard decision
  useEffect(() => {
    if (trip && showWizard === null) {
      const wizardReturn = sessionStorage.getItem(WIZARD_RETURN_KEY);
      if (wizardReturn === code) {
        sessionStorage.removeItem(WIZARD_RETURN_KEY);
        setShowWizard('rounds');
        return;
      }
      const isEmpty = (trip.players?.length || 0) === 0 && (trip.rounds?.length || 0) === 0;
      const notCompleted = trip.setup_completed !== true;
      setShowWizard(isEmpty && notCompleted && isOrganiser);
    }
  }, [trip, showWizard, code, isOrganiser]);

  const handleCopyCode = () => {
    navigator.clipboard?.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = () => {
    const url = `${window.location.origin}/join/${code}`;
    if (navigator.share) {
      navigator.share({ title: `Fore Tour™ — ${trip?.name}`, text: `Join with code ${code}`, url });
    } else {
      navigator.clipboard?.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const openRename = () => { setRenameDraft(trip?.name || ''); setRenameOpen(true); };
  const handleRename = async () => {
    const trimmed = renameDraft.trim();
    if (!trimmed || trimmed === trip?.name) { setRenameOpen(false); return; }
    setRenameSaving(true);
    try {
      const res = await patchTrip(code, { name: trimmed });
      updateTrip(res.data);
      setRenameOpen(false);
      toast.success('Event name updated');
    } catch {
      toast.error('Failed to update name');
    } finally { setRenameSaving(false); }
  };

  const handleAddRound = async () => {
    // Pre-check lifetime cap before making the API call
    if (!trip?.legacy_unlimited && trip?.max_rounds) {
      const used = trip.rounds_created ?? trip.rounds?.length ?? 0;
      if (used >= trip.max_rounds) {
        toast.error(`You've hit your rounds cap for this trip.`);
        if (trip.promo_code) {
          navigate(`/trip/${code}/upgrade?kind=rounds&delta_rounds=1`);
        } else {
          navigate(`/trip/${code}/extra-capacity?kind=rounds&delta_rounds=1`);
        }
        return;
      }
    }
    const label = `Round ${(trip?.rounds?.length || 0) + 1}`;
    const defaultCourse = { id: 'custom', name: 'Select Course', par: 72, course_rating: 72, slope_rating: 113 };
    try {
      const res = await addRound(code, { label, course: defaultCourse });
      updateTrip(res.data);
      const newRound = res.data.rounds[res.data.rounds.length - 1];
      navigate(`/trip/${code}/round/${newRound.id}/course`);
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.code === 'cap_exceeded') {
        toast.error(`You've hit your rounds cap for this trip.`);
        const hasPromo = e.response?.data?.has_promo;
        if (hasPromo) {
          navigate(`/trip/${code}/upgrade?kind=rounds&delta_rounds=1`);
        } else {
          navigate(`/trip/${code}/extra-capacity?kind=rounds&delta_rounds=1`);
        }
      }
    }
  };

  // Delete trip state
  const [deletePin, setDeletePin] = useState('');
  const [deleteConfirmCode, setDeleteConfirmCode] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const resetDeleteState = () => { setDeletePin(''); setDeleteConfirmCode(''); setDeleteError(''); };
  useEffect(() => { if (deleteOpen) resetDeleteState(); }, [deleteOpen]);

  const handleDeleteTrip = async () => {
    if (deleteConfirmCode.toUpperCase() !== code.toUpperCase()) {
      setDeleteError('Event code does not match.'); return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteTrip(code, { pin: deletePin || null, confirm_code: deleteConfirmCode.toUpperCase() });
      removeAccessedCode(code);
      navigate('/home');
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail === 'organiser_required') setDeleteError('Organiser access required.');
      else if (detail === 'invalid_pin') setDeleteError('Wrong organiser password.');
      else setDeleteError('Failed to delete.');
    } finally { setDeleting(false); }
  };

  // Delete round state
  const [roundDeleteOpen, setRoundDeleteOpen] = useState(null); // round object or null
  const [roundDeletePin, setRoundDeletePin] = useState('');
  const [roundDeleteError, setRoundDeleteError] = useState('');
  const [roundDeleting, setRoundDeleting] = useState(false);

  const resetRoundDeleteState = () => { setRoundDeletePin(''); setRoundDeleteError(''); };
  useEffect(() => { if (roundDeleteOpen) resetRoundDeleteState(); }, [roundDeleteOpen]);

  const handleDeleteRound = async () => {
    if (!roundDeleteOpen) return;
    setRoundDeleting(true);
    setRoundDeleteError('');
    try {
      const res = await deleteRound(code, roundDeleteOpen.id, { pin: roundDeletePin || null, confirm_round_id: roundDeleteOpen.id });
      updateTrip(res.data);
      setRoundDeleteOpen(null);
      setRoundDeletePin('');
    } catch (e) {
      const detail = e.response?.data?.detail || '';
      if (detail === 'organiser_required') setRoundDeleteError('Organiser access required.');
      else if (detail === 'invalid_pin') setRoundDeleteError('Wrong organiser password.');
      else if (detail === 'owner_required_for_scored_round') setRoundDeleteError('Master password required — round has scores.');
      else setRoundDeleteError('Failed to delete round.');
    } finally { setRoundDeleting(false); }
  };

  const handleToggleActive = async (playerId, newActive) => {
    setTogglingPlayer(playerId);
    try {
      const res = await patchPlayerActive(code, playerId, { active: newActive });
      updateTrip(res.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error('Organiser access required');
    } finally { setTogglingPlayer(null); }
  };

  const handleGateUnlock = (gateRole, gateLegacy) => {
    setRole(gateRole);
    setLegacy(gateLegacy);
    setNeedsGate(false);
    setAccessChecked(true);
    if (shouldShowHomeScreenTip()) setShowHomeScreenTip(true);
  };

  const joinUrl = `${JOIN_BASE || window.location.origin}/join?code=${code}`;

  // Show password gate if needed
  if (needsGate) return <TripPasswordGate code={code} brandingImageUrl={brandingImageUrl} coverImageUrl={coverImageUrl} onUnlock={handleGateUnlock} />;

  if (loading) return (
    <div className="min-h-screen bg-[#F4F6F5]" data-testid="trip-detail-skeleton">
      <div className="h-56 sm:h-64 bg-white animate-pulse border-b border-[#E1E6E4]" />
      <div className="px-4 pt-4 max-w-lg mx-auto space-y-3">
        <div className="h-6 w-48 bg-white rounded-lg animate-pulse border border-[#E1E6E4]" />
        <div className="h-4 w-32 bg-white rounded-lg animate-pulse border border-[#E1E6E4]" />
        {[1,2,3].map(i => <div key={i} className="h-24 bg-white rounded-xl animate-pulse border border-[#E1E6E4]" />)}
      </div>
    </div>
  );
  if (error) return <div className="min-h-screen bg-[#F4F6F5] flex flex-col items-center justify-center gap-4"><p className="text-[#D32F2F]">{error}</p><Button onClick={() => navigate('/home')} variant="outline">Go Home</Button></div>;
  if (!trip) return null;

  if (showWizard) return (
    <TripAccessContext.Provider value={{ role, legacy }}>
      <TripSetupWizard trip={trip} code={code} returnToStep={showWizard === 'rounds' ? 1 : 0} onComplete={(updated) => { setTrip(updated); setShowWizard(false); }} />
    </TripAccessContext.Provider>
  );

  return (
    <TripAccessContext.Provider value={{ role, legacy }}>
    <ScoringHomeScreenTip open={showHomeScreenTip} onDismiss={() => setShowHomeScreenTip(false)} />
    <div className="min-h-screen bg-[#F4F6F5] pb-safe" data-testid="trip-detail">
      {/* Hero */}
      <div className="relative h-56 sm:h-64 overflow-hidden">
        <img src={coverImageUrl || HERO_IMG} alt="Golf course" decoding="async" className="w-full h-full object-cover object-center" data-testid="hero-cover-img" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10">
          <button onClick={() => navigate('/home')} className="w-10 h-10 rounded-full glass-dark flex items-center justify-center" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5 text-white" weight="bold" />
          </button>
          <div className="flex gap-2">
            <button onClick={handleShare} className="w-10 h-10 rounded-full glass-dark flex items-center justify-center" data-testid="share-btn">
              <ShareNetwork className="w-5 h-5 text-white" weight="bold" />
            </button>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-[#1B4332] text-white text-xs px-2 py-0.5 rounded-md font-semibold">
              {EVENT_LABELS[trip.event_type] || 'Golf Trip'}
            </Badge>
            <Badge className={`text-xs px-2 py-0.5 rounded-md font-semibold ${isOrganiser ? 'bg-[#E59000] text-white' : 'bg-white/80 text-[#1D242B]'}`} data-testid="role-badge">
              {isOrganiser ? 'Organiser' : 'Player'}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl sm:text-3xl text-white font-bold" data-testid="trip-name-title">{trip.name}</h1>
            {isOrganiser && (
              <button onClick={openRename} className="text-white/70 hover:text-white transition-colors p-1" data-testid="rename-trip-btn">
                <Pencil className="w-4 h-4" weight="bold" />
              </button>
            )}
          </div>
          <button onClick={handleCopyCode} className="flex items-center gap-1.5 mt-2 text-[#E8E5DB] text-sm" data-testid="copy-code-btn">
            <span className="font-mono font-bold">{trip.code}</span>
            <Copy className="w-4 h-4" weight="bold" />
            {copied && <span className="text-xs text-[#1B4332] bg-white/90 px-2 py-0.5 rounded ml-1">Copied!</span>}
          </button>
        </div>
      </div>

      {/* Summary cards */}
      {brandingImageUrl && (
        <div className="px-4 -mt-2 relative z-10 max-w-lg mx-auto">
          <EventBranding imageUrl={brandingImageUrl} />
        </div>
      )}
      <div className={`px-4 ${brandingImageUrl ? 'mt-2' : '-mt-4'} relative z-10 max-w-lg mx-auto`}>
        <div className="grid grid-cols-3 gap-3">
          <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl text-center cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate(`/trip/${code}/players`)} data-testid="players-card">
            <UsersThree className="w-6 h-6 text-[#1B4332] mx-auto mb-1" weight="fill" />
            <p className="text-2xl font-bold text-[#1D242B]">{trip.players?.length || 0}</p>
            <p className="text-xs text-[#455561]">Players</p>
          </Card>
          <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl text-center cursor-pointer hover:shadow-md transition-shadow"
            data-testid="rounds-card">
            <Golf className="w-6 h-6 text-[#1B4332] mx-auto mb-1" weight="fill" />
            <p className="text-2xl font-bold text-[#1D242B]">{trip.rounds?.length || 0}</p>
            <p className="text-xs text-[#455561]">Rounds</p>
          </Card>
          <Card className="p-4 bg-white border-[#E1E6E4] rounded-xl text-center cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate(`/trip/${code}/league`)} data-testid="league-card">
            <Trophy className="w-6 h-6 text-[#E59000] mx-auto mb-1" weight="fill" />
            <p className="text-2xl font-bold text-[#1D242B]">
              {trip.rounds?.some(r => r.has_scores || Object.keys(r.hole_scores || {}).length > 0) ? 'View' : '--'}
            </p>
            <p className="text-xs text-[#455561]">League</p>
          </Card>
        </div>
      </div>

      {/* QR Code + Share section */}
      <div className="px-4 mt-3 max-w-lg mx-auto">
        <button onClick={() => setShowQR(!showQR)} className="flex items-center gap-2 text-sm text-[#455561] hover:text-[#1B4332] transition-colors" data-testid="toggle-qr-btn">
          <QrCode className="w-4 h-4" weight="bold" /> {showQR ? 'Hide QR' : 'Show QR code'}
        </button>
        {showQR && (
          <Card className="mt-2 p-4 bg-white border-[#E1E6E4] rounded-xl flex flex-col items-center gap-3" data-testid="qr-section">
            <QRCodeSVG value={joinUrl} size={180} fgColor="#1B4332" bgColor="transparent" level="M" />
            <p className="text-xs text-[#455561]">Scan to join</p>
            <div className="flex items-center gap-2 bg-[#F4F6F5] rounded-lg px-3 py-2 w-full">
              <span className="text-xs text-[#1D242B] font-mono truncate flex-1">{joinUrl}</span>
              <button onClick={() => { navigator.clipboard?.writeText(joinUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
                className="text-[#1B4332] flex-shrink-0" data-testid="copy-qr-url">
                <Copy className="w-4 h-4" weight="bold" />
              </button>
            </div>
            {/* Share user password — organiser only */}
            {isOrganiser && !legacy && (
              <div className="w-full bg-[#F4F6F5] rounded-lg p-3 mt-1" data-testid="share-user-password">
                <p className="text-xs text-[#455561] mb-1 font-semibold">Player code</p>
                <p className="text-xs text-[#8A9BA8]">Share this with your players. They use it to join the event and enter scores.</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <code className="bg-white px-2 py-1 rounded text-sm font-mono text-[#1D242B] flex-1">
                    {getTripAccess(code)?.password === (trip.pin || '') ? '(Ask your organiser)' : '••••'}
                  </code>
                  <p className="text-[10px] text-[#8A9BA8]">Visible at creation only</p>
                </div>
              </div>
            )}
          </Card>
        )}
      </div>

      {/* Rounds list */}
      <div className="px-4 pt-6 max-w-lg mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-[#1D242B]">Rounds</h2>
          {isOrganiser && (
            <Button onClick={handleAddRound} size="sm" className="bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl h-10 px-4" data-testid="add-round-btn">
              <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Add Round
            </Button>
          )}
        </div>

        {(!trip.rounds || trip.rounds.length === 0) ? (
          <Card className="p-8 bg-white border-[#E1E6E4] rounded-xl text-center">
            <Golf className="w-10 h-10 text-[#B8C7C0] mx-auto mb-3" />
            <p className="text-[#455561] text-sm">No rounds yet. {isOrganiser ? 'Add one to get started.' : 'Your organiser will add rounds.'}</p>
          </Card>
        ) : (
          <div className="space-y-3 stagger-children">
            {trip.rounds.map((round) => {
              const scoredPlayers = round.scored_player_count ?? Object.keys(round.hole_strokes || {}).filter(pid => {
                const strokes = (round.hole_strokes || {})[pid];
                return strokes && strokes.some(s => s > 0);
              }).length;
              const roundScoreLocked = roundHasScores(round);
              const frozen = isRoundFrozen(round);
              const allGroupsFinished = (() => {
                const t = round.teams || [];
                const gf = round.group_finished || [];
                return t.length > 0 && t.every((_, i) => gf[i] === true);
              })();
              const isRoundFinished = round.revealed === true
                ? true
                : frozen ? false
                : allGroupsFinished && scoredPlayers > 0;
              return (
                <Card key={round.id} className="bg-white border-[#E1E6E4] rounded-xl overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  data-testid={`round-card-${round.id}`}
                  onClick={() => navigate(`/trip/${code}/round/${round.id}/setup`)}>
                  <div className="p-4 flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-bold text-[#1D242B]">{round.label}</span>
                        {round.teams_locked && <Badge className="bg-[#E8E5DB] text-[#1B4332] text-[10px] px-1.5 py-0 font-semibold">Draw Locked</Badge>}
                        {isRoundFinished
                          ? <Badge className="bg-[#2E7D32] text-white text-[10px] px-1.5 py-0 font-semibold" data-testid="round-finished-badge">Finished</Badge>
                          : scoredPlayers > 0 && <Badge className="bg-[#E59000] text-white text-[10px] px-1.5 py-0 font-semibold">Scoring</Badge>
                        }
                        {roundScoreLocked && <Badge className="bg-[#FFF3E0] text-[#E59000] text-[10px] px-1.5 py-0 font-semibold border border-[#FFE0B2]"><Lock className="w-3 h-3 inline mr-0.5" weight="bold" />Owner locked</Badge>}
                      </div>
                      <p className="text-sm text-[#455561] truncate">{round.course?.name || 'No course selected'}</p>
                      <p className="text-xs text-[#B8C7C0] mt-1">Par {round.course?.par || '--'} · CR {round.course?.course_rating || '--'} · Slope {round.course?.slope_rating || '--'}</p>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {isOrganiser && (
                        <button onClick={(e) => {
                          e.stopPropagation();
                          setRoundDeleteOpen(round); setRoundDeletePin(''); setRoundDeleteError('');
                        }} className="p-2 transition-colors text-[#B8C7C0] hover:text-[#D32F2F]" data-testid={`delete-round-${round.id}`}>
                          <Trash className="w-4 h-4" weight="bold" />
                        </button>
                      )}
                      <CaretRight className="w-5 h-5 text-[#B8C7C0]" />
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Trip Teams */}
      <TripTeamsSection trip={trip} code={code} isOrganiser={isOrganiser} onUpdate={setTrip} />

      {/* Divisions */}
      <DivisionsEditor trip={trip} code={code} isOrganiser={isOrganiser} onUpdate={setTrip} />

      {/* Prize Pot */}
      <div className="px-4 pt-2 max-w-lg mx-auto">
        <PrizePot trip={trip} code={code} isOrganiser={isOrganiser} onUpdate={setTrip} />
      </div>

      {/* Event Branding (signed-in account organiser or owner only — not PIN-only) */}
      {(isSignedInOrganiser || isOwner) && !legacy && (
        <div className="px-4 pt-4 max-w-lg mx-auto">
          <EventBrandingCard trip={trip} code={code} onUpdate={updateTrip} />
        </div>
      )}

      {/* Quick actions */}
      <div className="px-4 pt-6 pb-8 max-w-lg mx-auto space-y-3">
        <Button onClick={() => navigate(`/trip/${code}/players`)} variant="outline"
          className="w-full h-14 rounded-xl border-[#E1E6E4] text-[#1D242B] justify-between text-base" data-testid="manage-players-btn">
          <span className="flex items-center gap-2"><UsersThree className="w-5 h-5 text-[#1B4332]" weight="fill" /> {isOrganiser ? 'Manage Players' : 'View Players'}</span>
          <CaretRight className="w-5 h-5 text-[#B8C7C0]" />
        </Button>
        <Button onClick={() => navigate(`/trip/${code}/league`)} variant="outline"
          className="w-full h-14 rounded-xl border-[#E1E6E4] text-[#1D242B] justify-between text-base" data-testid="view-league-btn">
          <span className="flex items-center gap-2"><Trophy className="w-5 h-5 text-[#E59000]" weight="fill" /> League Table</span>
          <CaretRight className="w-5 h-5 text-[#B8C7C0]" />
        </Button>
        {isOrganiser && (
          <Button onClick={() => setDeleteOpen(true)} variant="ghost"
            className="w-full h-14 text-[#D32F2F] hover:bg-red-50 rounded-xl" data-testid="delete-trip-btn">
            <Trash className="w-4 h-4 mr-2" weight="bold" /> Delete Event
          </Button>
        )}
      </div>

      {/* Scoring PIN — organiser only */}
      {isOrganiser && (
        <div className="px-4 pb-4 max-w-lg mx-auto" data-testid="scoring-pin-section">
          <Card className="bg-white border-[#E1E6E4] rounded-xl overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#1B4332]" weight="bold" />
                <span className="font-semibold text-sm text-[#1D242B]">Organiser code</span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold font-mono text-[#1D242B] tracking-widest" data-testid="current-pin-display">
                  {trip.pin || '—'}
                </span>
                <Button size="sm" variant="outline" className="rounded-xl h-8 px-3 text-xs"
                  data-testid="rotate-pin-btn"
                  onClick={() => { setNewPin(''); setRotateError(''); setRotateSuccess(null); setRotatePinOpen(true); }}>
                  Rotate
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Player code — organiser only */}
      {isOrganiser && (
        <div className="px-4 pb-4 max-w-lg mx-auto" data-testid="player-password-section">
          <Card className="bg-white border-[#E1E6E4] rounded-xl overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-[#1B4332]" weight="bold" />
                  <span className="font-semibold text-sm text-[#1D242B]">Player code</span>
                </div>
                <p className="text-xs text-[#8A9BA8] mt-0.5">Share this with your players. They use it to join the event and enter scores.</p>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-lg font-bold font-mono text-[#1D242B] tracking-widest" data-testid="current-user-password-display">
                  {trip.user_password || '—'}
                </span>
                <Button size="sm" variant="outline" className="rounded-xl h-8 px-3 text-xs"
                  data-testid="rotate-user-password-btn"
                  onClick={() => { setRotatePasswordResult(null); setRotatePasswordOpen(true); }}>
                  Rotate
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Rotate Player Password dialog */}
      <Dialog open={rotatePasswordOpen} onOpenChange={open => { if (!open) setRotatePasswordOpen(false); }}>
        <DialogContent className="sm:max-w-xs rounded-2xl" data-testid="rotate-user-password-dialog">
          <DialogHeader><DialogTitle>{rotatePasswordResult ? 'Password Rotated' : 'Rotate Player Password'}</DialogTitle></DialogHeader>
          {rotatePasswordResult ? (
            <div className="space-y-3 text-center py-2">
              <p className="text-2xl font-bold font-mono tracking-widest text-[#1B4332]" data-testid="new-user-password-display">{rotatePasswordResult}</p>
              <Button size="sm" variant="outline" className="rounded-xl"
                onClick={() => { navigator.clipboard?.writeText(rotatePasswordResult); toast.success('Copied'); }}
                data-testid="copy-new-user-password-btn">
                <Copy className="w-4 h-4 mr-1" weight="bold" /> Copy password
              </Button>
              <p className="text-xs text-[#455561]">Players already in the app can continue with the old password. New players should use this one.</p>
              <Button className="w-full bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl"
                onClick={() => setRotatePasswordOpen(false)} data-testid="close-rotate-password-dialog">Done</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#455561]">Generate a new random 8-character player code. The old code will keep working for players already using it.</p>
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setRotatePasswordOpen(false)} className="flex-1 rounded-xl">Cancel</Button>
                <Button disabled={rotatePasswordLoading}
                  className="flex-1 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl"
                  data-testid="confirm-rotate-user-password-btn"
                  onClick={async () => {
                    setRotatePasswordLoading(true);
                    try {
                      const res = await api.post(`/trips/${code}/rotate-user-password`);
                      setRotatePasswordResult(res.data.user_password);
                      const fresh = await getTrip(code);
                      setTrip(fresh.data);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || 'Failed to rotate password');
                    } finally { setRotatePasswordLoading(false); }
                  }}>
                  {rotatePasswordLoading ? 'Rotating...' : 'Generate new password'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Rotate PIN dialog */}
      <Dialog open={rotatePinOpen} onOpenChange={open => { if (!open) setRotatePinOpen(false); }}>

        <DialogContent className="sm:max-w-xs rounded-2xl" data-testid="rotate-pin-dialog">
          <DialogHeader><DialogTitle>{rotateSuccess ? 'Code Rotated' : 'Rotate Organiser Code'}</DialogTitle></DialogHeader>
          {rotateSuccess ? (
            <div className="space-y-3 text-center py-2">
              <p className="text-3xl font-bold font-mono tracking-widest text-[#1B4332]" data-testid="new-pin-display">{rotateSuccess}</p>
              <Button size="sm" variant="outline" className="rounded-xl"
                onClick={() => { navigator.clipboard?.writeText(rotateSuccess); toast.success('Copied'); }}
                data-testid="copy-new-pin-btn">
                <Copy className="w-4 h-4 mr-1" weight="bold" /> Copy PIN
              </Button>
              <p className="text-xs text-[#E59000] font-semibold">All players currently using the Organiser code will need to re-enter this new code.</p>
              <Button className="w-full bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl"
                onClick={() => setRotatePinOpen(false)} data-testid="close-rotate-dialog">Done</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-[#455561]">Enter a new 4–6 digit numeric PIN.</p>
              <Input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                value={newPin} onChange={e => { setNewPin(e.target.value.replace(/\D/g, '').slice(0, 6)); setRotateError(''); }}
                placeholder="New PIN" className="text-center text-2xl font-bold tracking-widest h-14 rounded-xl"
                data-testid="new-pin-input" />
              {rotateError && <p className="text-xs text-[#D32F2F] font-semibold" data-testid="rotate-pin-error">{rotateError}</p>}
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => setRotatePinOpen(false)} className="flex-1 rounded-xl">Cancel</Button>
                <Button disabled={rotateLoading || newPin.length < 4}
                  className="flex-1 bg-[#1B4332] hover:bg-[#163B2B] text-white rounded-xl"
                  data-testid="confirm-rotate-btn"
                  onClick={async () => {
                    if (!/^\d{4,6}$/.test(newPin)) { setRotateError('PIN must be 4–6 digits'); return; }
                    setRotateLoading(true); setRotateError('');
                    try {
                      const res = await rotateUserPin(code, { new_pin: newPin });
                      setRotateSuccess(res.data.pin);
                      // Update organiser's stored credential to the new PIN
                      setTripAccess(code, 'organiser', res.data.pin);
                      // Refresh trip data
                      const fresh = await getTrip(code);
                      setTrip(fresh.data);
                    } catch (e) {
                      const detail = e.response?.data?.detail || 'Failed to rotate PIN';
                      setRotateError(detail === 'invalid_pin_format' ? 'PIN must be 4–6 digits' : detail);
                    } finally { setRotateLoading(false); }
                  }}>
                  {rotateLoading ? 'Rotating...' : 'Confirm'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Player access management — organiser only, no PIN prompt */}
      {isOrganiser && (trip.players?.length > 0) && (
        <div className="px-4 pb-4 max-w-lg mx-auto">
          <Card className="bg-white border-[#E1E6E4] rounded-xl overflow-hidden" data-testid="player-access-section">
            <div className="p-4 border-b border-[#E1E6E4]">
              <div className="flex items-center gap-2">
                <LockOpen className="w-4 h-4 text-[#1B4332]" weight="bold" />
                <span className="font-semibold text-sm text-[#1D242B]">Manage player access</span>
              </div>
            </div>
            <div className="divide-y divide-[#F4F6F5]">
              {(trip.players || []).map(player => {
                const isActive = player.active !== false;
                return (
                  <div key={player.id} className={`flex items-center gap-3 px-4 py-3 ${!isActive ? 'opacity-50' : ''}`}
                    data-testid={`access-row-${player.id}`}>
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-[#1D242B] truncate block">{player.name}</span>
                      <span className="text-[10px] text-[#455561]">WHS {displayWHS(player)}</span>
                    </div>
                    {!isActive && <Badge className="bg-[#F4F6F5] text-[#B8C7C0] text-[10px] flex-shrink-0">Inactive</Badge>}
                    <Switch
                      checked={isActive}
                      disabled={togglingPlayer === player.id}
                      onCheckedChange={(v) => handleToggleActive(player.id, v)}
                      data-testid={`access-toggle-${player.id}`}
                    />
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}

      <Dialog open={deleteOpen} onOpenChange={(open) => { if (!open) { setDeleteOpen(false); resetDeleteState(); } }}>
        <DialogContent className="sm:max-w-sm rounded-2xl">
          <DialogHeader><DialogTitle>Delete Event?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#455561] py-1">This will <strong>permanently</strong> delete <strong>{trip.name}</strong> and all its data. This cannot be undone.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-[#455561] block mb-1">Organiser Password</label>
              <Input type="password" value={deletePin} onChange={(e) => { setDeletePin(e.target.value); setDeleteError(''); }}
                placeholder="Enter organiser password" className="h-10 rounded-lg text-sm" autoComplete="off" data-testid="delete-trip-pin" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#455561] block mb-1">Type event code to confirm: <span className="font-mono text-[#1D242B]">{code}</span></label>
              <Input value={deleteConfirmCode} onChange={(e) => { setDeleteConfirmCode(e.target.value); setDeleteError(''); }}
                placeholder={`Type ${code} to confirm`} className="h-10 rounded-lg text-sm font-mono" autoComplete="off" data-testid="delete-trip-confirm-code" />
            </div>
            {deleteError && <p className="text-xs text-[#D32F2F]" data-testid="delete-trip-error">{deleteError}</p>}
          </div>
          <DialogFooter className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => { setDeleteOpen(false); resetDeleteState(); }} className="flex-1 rounded-xl">Cancel</Button>
            <Button onClick={handleDeleteTrip}
              disabled={deleting || !deleteConfirmCode.trim() || deleteConfirmCode.toUpperCase() !== code.toUpperCase()}
              className="flex-1 bg-[#D32F2F] hover:bg-red-700 text-white rounded-xl" data-testid="confirm-delete-btn">
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Round confirmation dialog */}
      <Dialog open={!!roundDeleteOpen} onOpenChange={(open) => { if (!open) { setRoundDeleteOpen(null); resetRoundDeleteState(); } }}>
        <DialogContent className="sm:max-w-sm rounded-2xl">
          <DialogHeader><DialogTitle>Delete Round?</DialogTitle></DialogHeader>
          <p className="text-sm text-[#455561] py-1">This will permanently delete <strong>{roundDeleteOpen?.label}</strong> and all its scores.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-[#455561] block mb-1">Organiser Password</label>
              <Input type="password" value={roundDeletePin} onChange={(e) => { setRoundDeletePin(e.target.value); setRoundDeleteError(''); }}
                placeholder="Enter organiser password" className="h-10 rounded-lg text-sm" autoComplete="off" data-testid="delete-round-pin" />
            </div>
            {roundDeleteError && <p className="text-xs text-[#D32F2F]" data-testid="delete-round-error">{roundDeleteError}</p>}
          </div>
          <DialogFooter className="flex gap-2 mt-2">
            <Button variant="outline" onClick={() => { setRoundDeleteOpen(null); resetRoundDeleteState(); }} className="flex-1 rounded-xl">Cancel</Button>
            <Button onClick={handleDeleteRound} disabled={roundDeleting}
              className="flex-1 bg-[#D32F2F] hover:bg-red-700 text-white rounded-xl" data-testid="confirm-delete-round-btn">
              {roundDeleting ? 'Deleting...' : 'Delete Round'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename event dialog */}
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="sm:max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#1D242B]">Rename Event</DialogTitle>
          </DialogHeader>
          <Input
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            maxLength={60}
            className="h-12 rounded-xl text-base"
            placeholder="Event name"
            autoFocus
            onKeyDown={(e) => e.key === 'Enter' && renameDraft.trim() && handleRename()}
            data-testid="rename-trip-input"
          />
          <DialogFooter className="flex gap-3 sm:gap-3">
            <Button variant="outline" onClick={() => setRenameOpen(false)} className="flex-1 h-11 rounded-xl border-[#E1E6E4] text-[#455561] font-semibold" data-testid="rename-cancel-btn">
              Cancel
            </Button>
            <Button onClick={handleRename} disabled={!renameDraft.trim() || renameSaving}
              className="flex-1 h-11 rounded-xl bg-[#1B4332] hover:bg-[#163B2B] text-white font-semibold" data-testid="rename-save-btn">
              {renameSaving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </TripAccessContext.Provider>
  );
}
